import { AnalyticsEventName } from '@dupe/common/constants'
import { getShopKey } from '@dupe/common/helpers'
import type { SerpLensResponse } from '@dupe/common/types'
import React, { useEffect, useMemo, useState } from 'react'
import DupeResultCard from '~/src/components/DupeResultCard'
import OurPicksContrainer from '~/src/components/OurPicksContrainer'
import SkeletonLoader from '~/src/components/SkeletonLoader'
import type { ResultsResp } from '~/src/db/queryResultsResp'
import useAnalytics from '~/src/hooks/useAnalytics'
import { cn } from '~/src/lib/utils'
import useDeviceDetect from '~/src/hooks/useDeviceDetect'
import { useClickSource } from '~/src/context/ClickSourceContext'
import { useResultSortContext } from '~/src/context/ResultSortContext'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  resultsData: Partial<ResultsResp> | null
  ourPicks: SerpLensResponse['visual_matches']
  source?: string
  loadingDupes?: boolean
  borderNone?: boolean
  initialInView?: boolean
  picksBackground?: boolean
  selectedCountry?: string
  query?: string | null
  query_category?: string | null
}

function isWayfairLink(link: string | null | undefined): boolean {
  const shopKey = getShopKey(link)
  if (!shopKey) return false

  try {
    const host = new URL(shopKey).hostname
    return host === 'wayfair.com' || host.endsWith('.wayfair.com')
  } catch {
    return false
  }
}

const OurPicks: React.FC<Props> = ({
  resultsData,
  ourPicks,
  source: baseSource = 'ourpicks',
  loadingDupes,
  borderNone,
  initialInView,
  picksBackground = true,
  selectedCountry,
  query,
  query_category,
  className,
  ...props
}) => {
  const analytics = useAnalytics()
  const { isMobile } = useDeviceDetect()
  // Prefix source via ClickSourceContext when rendered inside a surface-specific
  // wrapper (e.g. extension sidepanel -> 'ext-ourpicks' / 'ext-drawer-ourpicks').
  const source = useClickSource(baseSource)
  // Partner-pin test arm (null outside the test, and on every surface that
  // does not provide one). Stamped only when present so control / legacy
  // payloads stay byte-for-byte what they were.
  const { sortCriteria, defaultSortVariant, sortReady } = useResultSortContext()
  const armDsv = sortReady ? defaultSortVariant : null

  const [matches, setMatches] = useState(ourPicks || [])

  const placeholderLength = matches?.length || 4
  const placeholderItems = useMemo(
    () => Array.from({ length: placeholderLength }).map((_, i) => ({ id: i })),
    [placeholderLength]
  )

  // Filter to keep only one Wayfair result on mobile
  useEffect(() => {
    // This mobile check to only display one Wayfair result is used due to lower commsions from wayfair on mobile due to wayfair mobile app commission rate.
    if (isMobile) {
      // Filter to keep only one Wayfair result
      const filteredPicks = [...ourPicks]
      const wayfairIndices = filteredPicks
        .map((pick, index) => (isWayfairLink(pick.link) ? index : -1))
        .filter((index) => index !== -1)

      // Remove extra Wayfair results if more than one exists
      if (wayfairIndices.length > 1) {
        wayfairIndices
          .slice(1)
          .reverse()
          .forEach((index) => {
            filteredPicks.splice(index, 1)
          })
      }

      setMatches(filteredPicks)
    } else {
      setMatches(ourPicks)
    }
  }, [ourPicks, isMobile])

  if (!loadingDupes && !matches.length && !borderNone) {
    return <div className='border-t border-t-black/10' />
  }

  return (
    <OurPicksContrainer
      className={cn(className)}
      borderNone={borderNone}
      picksBackground={picksBackground}
      {...props}
    >
      <div className='space-y-4'>
        <div className='space-y-3 md:space-y-4'>
          <div>
            {loadingDupes &&
              placeholderItems.map((item) => (
                <div
                  key={item.id}
                  className={cn(
                    'flex space-x-2 px-4 pb-4 md:space-x-4 md:px-6 md:pb-6'
                  )}
                >
                  <div className='shrink-0'>
                    <SkeletonLoader
                      height='100%'
                      width='100%'
                      className='min-h-[115px] min-w-[115px] rounded-lg md:min-h-[160px] md:min-w-[160px]'
                    />
                  </div>
                  <div className='flex w-full flex-col'>
                    <div className='flex-1 space-y-2'>
                      <SkeletonLoader
                        height={20}
                        width={200}
                        className='rounded'
                      />
                      <SkeletonLoader height={20} className='rounded' />
                    </div>
                    <div>
                      <SkeletonLoader
                        height={33}
                        width={125}
                        className='rounded'
                      />
                    </div>
                  </div>
                </div>
              ))}

            {!loadingDupes &&
              matches.map((match, index) => {
                const shopKey = match.shopKey || getShopKey(match.link)
                return (
                  <DupeResultCard
                    key={`${index}.${match.position}.${match.link}`}
                    resultsData={resultsData}
                    index={index}
                    match={match}
                    isOurPick
                    initialInView={initialInView}
                    source={source}
                    className={cn(
                      'px-4 py-4 md:px-6 md:py-6 border-b border-black/10 dark:border-white/10'
                    )}
                    selectedCountry={selectedCountry}
                    query={query}
                    query_category={query_category}
                    {...(armDsv ? { dsv: armDsv } : {})}
                    onOutboundClick={({ clickId }) => {
                      analytics.track(
                        AnalyticsEventName.DupeResultClicked,
                        {
                          source,
                          click_id: clickId,
                          shoppingSource: match.shopping_source,
                          hash: resultsData?.hash,
                          shopKey,
                          ourPicks: true,
                          tier: match.tier,
                          result: match,
                          resultIndex: index,
                          picksIndex: index,
                          isPartner: !!match?.verified,
                          ...(armDsv
                            ? {
                                sort_criteria: sortCriteria,
                                default_sort_variant: armDsv,
                              }
                            : {}),
                        },
                        {
                          sendToPlausible: true,
                          sendToTinybird: true,
                          sendToFb: true,
                        }
                      )
                    }}
                  />
                )
              })}
          </div>
        </div>
      </div>
    </OurPicksContrainer>
  )
}

export default React.memo(OurPicks)
